import { normalizeRetailPricingDiscountFields } from "./proposalDiscountMethods";
import { parsePositiveProposalCurrencyAmount } from "./proposalCurrencyAmount";
import {
  gstInclusivePayableComponents,
  isGstInclusiveFinalAgreedPrice,
  resolveAgreedPriceBasis,
} from "./proposalAgreedPriceBasis";

export const PROPOSAL_INVOICE_GST_RATE = 0.09;

export type RetailPricingJson = {
  listTotal?: number | null;
  originalListPrice?: number | null;
  agreedPriceSgd?: number | null;
  agreedPrice?: number | null;
  /**
   * Staff-entered Final agreed price figure for Revise reopen.
   * When agreedPriceBasis is gst_inclusive, this is the nett including GST;
   * agreedPriceSgd remains the canonical ex-GST amount.
   */
  enteredAgreedPriceSgd?: number | null;
  /** Explicit GST basis for Final agreed price. Missing → before_gst. */
  agreedPriceBasis?: "before_gst" | "gst_inclusive" | null;
  specialDiscountSgd?: number | null;
  /** Explicit staff pricing method — see shared/proposalDiscountMethods.ts. */
  discountMethod?: "percentage" | "fixed_amount" | "final_agreed_price" | null;
  /** Staff-entered percentage when discountMethod is "percentage". */
  discountPercentage?: number | null;
  showDiscountPctToCustomer?: boolean | null;
  /** When false, hide per-line prices on customer-facing retail proposals (footer total still shown). */
  pricesVisibleToCustomer?: boolean | null;
  /**
   * Internal proposal MARK UP — never expose to customers / PDF / email.
   * When enabled, eligible hardware selling prices = base × (1 + markupPercentage/100).
   */
  markupEnabled?: boolean | null;
  markupPercentage?: number | null;
};

/** Retail catalogue redact-pricing: hide line prices; proposal total remains visible. */
export function resolveRetailPricesVisibleToCustomer(
  retailPricingJson?: RetailPricingJson | null,
): boolean {
  return retailPricingJson?.pricesVisibleToCustomer !== false;
}

export type ProposalCustomerPricingDisplay = {
  proposalTotal: number;
  finalAgreedPrice: number;
  showFinalAgreedPrice: boolean;
  /** max(0, proposalTotal − finalAgreedPrice). */
  discountAmount: number;
  /** Percentage of proposalTotal represented by discountAmount (0–100). */
  discountPercentage: number;
  /** True when a customer-facing discount row should render. */
  showDiscount: boolean;
  /** True when showDiscountPctToCustomer is set and a discount exists. */
  showDiscountPercentage: boolean;
  invoiceGstEnabled: boolean;
  gstAmount: number;
  showGst: boolean;
  finalAmountPayable: number;
  showFinalAmountPayable: boolean;
};

/** Customer-facing discount row label, optionally including percentage. */
export function formatCustomerPricingDiscountLabel(pricing: {
  showDiscountPercentage: boolean;
  discountPercentage: number;
}): string {
  if (!pricing.showDiscountPercentage) return "Discount";
  const pct = Number(pricing.discountPercentage);
  if (!Number.isFinite(pct) || pct <= 0) return "Discount";
  const rounded = Math.round(pct * 100) / 100;
  const text = Number.isInteger(rounded)
    ? String(Math.round(rounded))
    : rounded.toFixed(2);
  return `Discount (${text}%)`;
}

function roundMoney(n: number): number {
  if (!Number.isFinite(n)) return 0;
  return Math.round(n * 100) / 100;
}

function moneyNumber(value: unknown): number {
  const n = typeof value === "number" ? value : Number(value ?? 0);
  return Number.isFinite(n) ? n : 0;
}

/** First finite money value — treats NaN as missing (unlike `??`). */
export function coalesceMoneyNumber(...values: unknown[]): number | null {
  for (const value of values) {
    if (value == null) continue;
    const n = typeof value === "number" ? value : Number(value);
    if (Number.isFinite(n)) return n;
  }
  return null;
}

/** Parse currency strings ($89,765.00) or numeric proposal totals. Returns null for zero/invalid. */
export function parseProposalMoneyAmount(raw: unknown): number | null {
  if (typeof raw === "number") {
    return Number.isFinite(raw) && raw > 0 ? raw : null;
  }
  if (typeof raw === "string") {
    const trimmed = raw.trim();
    if (!trimmed) return null;
    const digits = trimmed.replace(/[^0-9.-]/g, "");
    if (!digits) return null;
    const n = Number(digits);
    return Number.isFinite(n) && n > 0 ? n : null;
  }
  return null;
}

/** First finite money > 0 — skips zero/negative (synthesized missing list totals). */
export function coalescePositiveMoneyNumber(...values: unknown[]): number | null {
  for (const value of values) {
    const parsed = parseProposalMoneyAmount(value);
    if (parsed != null) return parsed;
  }
  return null;
}

export function normalizeFiniteMoney(value: unknown, fallback = 0): number {
  return roundMoney(coalesceMoneyNumber(value, fallback) ?? fallback);
}

/** True when a negotiated Final Agreed Price is stored separately from list total. */
export function hasNegotiatedAgreedPrice(retailPricing: RetailPricingJson | null | undefined): boolean {
  return retailPricing?.agreedPriceSgd != null || retailPricing?.agreedPrice != null;
}

/** Σ persisted proposal_items.lineTotal — fallback when listTotal is absent. */
export function sumProposalItemLineTotals(items: Array<{ lineTotal?: unknown }>): number {
  const sum = items.reduce((acc, item) => acc + moneyNumber(item.lineTotal), 0);
  return roundMoney(sum);
}

/**
 * Resolve customer-facing Proposal Total.
 * When negotiated, never fall back to totalAmount (invoice base / agreed price).
 */
export function resolveProposalTotalAmount(input: {
  totalAmount?: unknown;
  retailPricingJson?: RetailPricingJson | null;
  computedProposalTotal?: unknown;
  lineItemTotalSum?: unknown;
  /** D-Tools hierarchy snapshot total (formatted string or numeric). Last-resort list total. */
  snapshotProposalTotal?: unknown;
}): number {
  const retailPricing = input.retailPricingJson ?? null;
  const negotiated = hasNegotiatedAgreedPrice(retailPricing);
  const fromLines = coalescePositiveMoneyNumber(input.computedProposalTotal, input.lineItemTotalSum);
  const fromSnapshot = parseProposalMoneyAmount(input.snapshotProposalTotal);

  if (negotiated) {
    const fromRetail = coalescePositiveMoneyNumber(
      retailPricing?.listTotal,
      retailPricing?.originalListPrice,
    );
    return roundMoney(coalescePositiveMoneyNumber(fromRetail, fromLines, fromSnapshot) ?? 0);
  }

  const fromRetail = coalescePositiveMoneyNumber(retailPricing?.listTotal, retailPricing?.originalListPrice);
  return roundMoney(
    coalescePositiveMoneyNumber(fromRetail, fromLines, fromSnapshot, input.totalAmount) ??
      coalesceMoneyNumber(input.totalAmount, 0) ??
      0,
  );
}

function moneyToCents(n: number): number {
  return Math.round(normalizeFiniteMoney(n) * 100);
}

function centsToMoney(cents: number): number {
  return roundMoney(cents / 100);
}

/** Allocate `total` across percentages so the parts sum exactly; remainder goes to the last row. */
export function allocateExactMoneyByPercentages(total: number, percentages: number[]): number[] {
  const totalCents = moneyToCents(total);
  if (percentages.length === 0) return [];
  const out: number[] = [];
  let allocated = 0;
  for (let i = 0; i < percentages.length - 1; i++) {
    const pct = Math.max(0, normalizeFiniteMoney(percentages[i]));
    const cents = Math.round((totalCents * pct) / 100);
    out.push(cents);
    allocated += cents;
  }
  out.push(totalCents - allocated);
  return out.map(centsToMoney);
}

/** Instalment slice of Final Amount Payable (incl. GST when enabled). */
export function resolveInstalmentPayableSlice(finalAmountPayable: number, instalmentPercentage: number): number {
  return allocateExactMoneyByPercentages(finalAmountPayable, [instalmentPercentage, 100 - instalmentPercentage])[0] ?? 0;
}

export type InstalmentScheduleRow = {
  stageLabel: string;
  percentage: number;
  exGstBase: number;
  gstAmount: number;
  payableWithGst: number;
};

/** Full payment schedule from Final Amount Payable (all instalments). */
export function buildInstalmentScheduleFromFinalPayable(input: {
  finalAmountPayable: number;
  invoiceGstEnabled: boolean;
  paymentTerms: Array<{ stage: string; percentage: number }>;
  /** Canonical proposal ex-GST commercial amount. Required for GST conservation. */
  proposalExGstBase?: number;
  proposalGstAmount?: number;
}): InstalmentScheduleRow[] {
  const gstOn = input.invoiceGstEnabled === true;
  const percentages = input.paymentTerms.map((term) => normalizeFiniteMoney(term.percentage));
  const payables = allocateExactMoneyByPercentages(input.finalAmountPayable, percentages);
  const proposalExGstBase = normalizeFiniteMoney(
    input.proposalExGstBase ??
      (gstOn ? roundMoney(input.finalAmountPayable / (1 + PROPOSAL_INVOICE_GST_RATE)) : input.finalAmountPayable),
  );
  const proposalGstAmount = normalizeFiniteMoney(
    input.proposalGstAmount ?? roundMoney(input.finalAmountPayable - proposalExGstBase),
  );
  const bases = gstOn
    ? allocateExactMoneyByPercentages(proposalExGstBase, percentages)
    : payables.slice();
  const gstParts = gstOn
    ? allocateExactMoneyByPercentages(proposalGstAmount, percentages)
    : percentages.map(() => 0);

  return input.paymentTerms.map((term, idx) => {
    const percentage = percentages[idx] ?? 0;
    const payableWithGst = payables[idx] ?? 0;
    let exGstBase = bases[idx] ?? 0;
    let gstAmount = gstOn ? (gstParts[idx] ?? 0) : 0;
    if (gstOn) {
      // Preserve the already-authoritative payable; never rewrite it from rounded GST.
      gstAmount = roundMoney(payableWithGst - exGstBase);
    } else {
      exGstBase = payableWithGst;
      gstAmount = 0;
    }
    return {
      stageLabel: String(term.stage ?? "").trim() || "Stage",
      percentage,
      exGstBase,
      gstAmount,
      payableWithGst,
    };
  });
}

/** Ex-GST invoice base for one instalment — derived from Final Amount Payable authority. */
export function resolveFirstInstalmentInvoiceBaseFromPayable(input: {
  finalAmountPayable: number;
  invoiceGstEnabled: boolean;
  instalmentPercentage: number;
  proposalExGstBase?: number;
  proposalGstAmount?: number;
}): number {
  const pct = normalizeFiniteMoney(input.instalmentPercentage);
  const remainder = roundMoney(100 - pct);
  const paymentTerms =
    remainder > 0.004
      ? [
          { stage: "Instalment", percentage: pct },
          { stage: "Remainder", percentage: remainder },
        ]
      : [{ stage: "Instalment", percentage: pct }];
  const schedule = buildInstalmentScheduleFromFinalPayable({
    finalAmountPayable: input.finalAmountPayable,
    invoiceGstEnabled: input.invoiceGstEnabled,
    paymentTerms,
    proposalExGstBase: input.proposalExGstBase,
    proposalGstAmount: input.proposalGstAmount,
  });
  return schedule[0]?.exGstBase ?? 0;
}

export function computeProposalFinalAmountPayable(
  baseAmount: number,
  invoiceGstEnabled: boolean,
): { gstAmount: number; finalAmountPayable: number; showGst: boolean } {
  const showGst = invoiceGstEnabled === true;
  const gstAmount = showGst ? roundMoney(baseAmount * PROPOSAL_INVOICE_GST_RATE) : 0;
  return {
    gstAmount,
    finalAmountPayable: roundMoney(baseAmount + gstAmount),
    showGst,
  };
}

/**
 * Customer-facing proposal pricing for initial vs negotiated proposals.
 *
 * Final price is resolved via the shared discount-method authority
 * (`normalizeRetailPricingDiscountFields`). Downstream must not reapply
 * percentage or fixed discount independently — `agreedPriceSgd` is canonical.
 */
export function resolveProposalCustomerPricingDisplay(input: {
  totalAmount?: unknown;
  retailPricingJson?: RetailPricingJson | null;
  invoiceGstEnabled?: boolean | null;
  /** Line-calculated total when listTotal is not stored (e.g. D-Tools JSON). */
  computedProposalTotal?: unknown;
  /** Σ proposal_items.lineTotal when computed total is unavailable. */
  lineItemTotalSum?: unknown;
  /** D-Tools hierarchy snapshot total (formatted string or numeric). */
  snapshotProposalTotal?: unknown;
}): ProposalCustomerPricingDisplay {
  const retailPricing = input.retailPricingJson ?? null;
  const proposalTotal = resolveProposalTotalAmount(input);

  const normalized = normalizeRetailPricingDiscountFields({
    proposalTotal,
    retail: retailPricing,
    invoiceGstEnabled: input.invoiceGstEnabled,
  });

  let finalAgreedPrice: number;
  let discountAmount: number;
  let discountPercentage: number;
  let showDiscountPercentage: boolean;
  const negotiated = hasNegotiatedAgreedPrice(retailPricing);

  if (normalized.ok) {
    finalAgreedPrice = normalized.normalized.finalAgreedPrice;
    discountAmount = normalized.normalized.discountAmount;
    discountPercentage = normalized.normalized.derivedPercentage;
    if (normalized.normalized.discountMethod === "percentage") {
      showDiscountPercentage =
        discountAmount > 0.004 && retailPricing?.showDiscountPctToCustomer !== false;
    } else {
      showDiscountPercentage =
        discountAmount > 0.004 && retailPricing?.showDiscountPctToCustomer === true;
    }
    if (negotiated) {
      const parsedAgreed = parsePositiveProposalCurrencyAmount(
        retailPricing?.agreedPriceSgd ?? retailPricing?.agreedPrice ?? normalized.normalized.agreedPriceSgd,
      );
      if (parsedAgreed.ok) {
        finalAgreedPrice = parsedAgreed.amount;
        discountAmount = roundMoney(Math.max(0, proposalTotal - finalAgreedPrice));
        discountPercentage =
          proposalTotal > 0.004 ? roundMoney((discountAmount / proposalTotal) * 100) : 0;
      }
    }
  } else if (negotiated) {
    const parsedAgreed = parsePositiveProposalCurrencyAmount(
      retailPricing?.agreedPriceSgd ?? retailPricing?.agreedPrice,
    );
    if (parsedAgreed.ok) {
      finalAgreedPrice = parsedAgreed.amount;
      discountAmount = roundMoney(Math.max(0, proposalTotal - finalAgreedPrice));
      discountPercentage =
        proposalTotal > 0.004 ? roundMoney((discountAmount / proposalTotal) * 100) : 0;
      showDiscountPercentage =
        discountAmount > 0.004 && retailPricing?.showDiscountPctToCustomer === true;
    } else {
      finalAgreedPrice = roundMoney(proposalTotal);
      discountAmount = 0;
      discountPercentage = 0;
      showDiscountPercentage = false;
    }
  } else {
    finalAgreedPrice = roundMoney(proposalTotal);
    discountAmount = 0;
    discountPercentage = 0;
    showDiscountPercentage = false;
  }

  const showDiscount = discountAmount > 0.004;
  const showFinalAgreedPrice = showDiscount;
  const invoiceGstEnabled = input.invoiceGstEnabled === true;
  const gstInclusive = isGstInclusiveFinalAgreedPrice({
    discountMethod: retailPricing?.discountMethod ?? (negotiated ? "final_agreed_price" : null),
    agreedPriceBasis: resolveAgreedPriceBasis(retailPricing),
    invoiceGstEnabled,
  });

  let gstAmount: number;
  let finalAmountPayable: number;
  let showGst: boolean;
  if (gstInclusive) {
    const enteredRaw = coalesceMoneyNumber(
      retailPricing?.enteredAgreedPriceSgd,
      retailPricing?.agreedPriceSgd,
      retailPricing?.agreedPrice,
      finalAgreedPrice,
    );
    const components = gstInclusivePayableComponents(enteredRaw ?? finalAgreedPrice);
    const storedCanonical = coalesceMoneyNumber(retailPricing?.agreedPriceSgd, retailPricing?.agreedPrice);
    if (
      storedCanonical != null &&
      retailPricing?.enteredAgreedPriceSgd != null &&
      Math.abs(storedCanonical - components.canonicalExGst) <= 0.02
    ) {
      finalAgreedPrice = roundMoney(storedCanonical);
    } else if (retailPricing?.enteredAgreedPriceSgd != null) {
      finalAgreedPrice = components.canonicalExGst;
    } else {
      // Unsaved draft: agreedPriceSgd still holds the staff-entered nett.
      finalAgreedPrice = components.canonicalExGst;
    }
    discountAmount = roundMoney(Math.max(0, proposalTotal - finalAgreedPrice));
    discountPercentage =
      proposalTotal > 0.004 ? roundMoney((discountAmount / proposalTotal) * 100) : 0;
    gstAmount = components.gstAmount;
    finalAmountPayable = components.finalAmountPayable;
    showGst = true;
  } else {
    const payable = computeProposalFinalAmountPayable(finalAgreedPrice, invoiceGstEnabled);
    gstAmount = payable.gstAmount;
    finalAmountPayable = payable.finalAmountPayable;
    showGst = payable.showGst;
  }
  const showFinalAmountPayable = showFinalAgreedPrice || showGst;

  return {
    proposalTotal: normalizeFiniteMoney(proposalTotal),
    finalAgreedPrice: normalizeFiniteMoney(finalAgreedPrice),
    showFinalAgreedPrice,
    discountAmount: normalizeFiniteMoney(discountAmount),
    discountPercentage: normalizeFiniteMoney(discountPercentage),
    showDiscount: discountAmount > 0.004,
    showDiscountPercentage,
    invoiceGstEnabled,
    gstAmount: normalizeFiniteMoney(gstAmount),
    showGst,
    finalAmountPayable: normalizeFiniteMoney(finalAmountPayable),
    showFinalAmountPayable,
  };
}

export type ProposalApprovalAuditAmounts = {
  proposalTotal: number;
  finalAgreedPrice: number;
  /** max(0, proposalTotal − finalAgreedPrice). Optional on legacy call sites. */
  discountAmount?: number;
  discountPercentage?: number;
  showDiscount?: boolean;
  showDiscountPercentage?: boolean;
  gstAmount: number;
  finalPayableAmount: number;
  invoiceGstEnabled: boolean;
};

/** When resolver proposalTotal is zero but snapshot has a valid D-Tools total, prefer snapshot. */
export function enrichProposalCustomerPricingWithSnapshot(
  pricing: ProposalCustomerPricingDisplay,
  snapshotProposalTotal?: unknown,
): ProposalCustomerPricingDisplay {
  const snapshotAmount = parseProposalMoneyAmount(snapshotProposalTotal);
  if (
    snapshotAmount != null &&
    (!Number.isFinite(pricing.proposalTotal) || pricing.proposalTotal <= 0)
  ) {
    const proposalTotal = roundMoney(snapshotAmount);
    const discountAmount = roundMoney(Math.max(0, proposalTotal - pricing.finalAgreedPrice));
    const showDiscount = discountAmount > 0.004;
    const discountPercentage =
      proposalTotal > 0.004 ? roundMoney((discountAmount / proposalTotal) * 100) : 0;
    return {
      ...pricing,
      proposalTotal,
      discountAmount,
      discountPercentage,
      showDiscount,
      showDiscountPercentage: showDiscount && pricing.showDiscountPercentage,
      showFinalAgreedPrice: showDiscount,
    };
  }
  return pricing;
}

export function resolveProposalApprovalAuditAmounts(input: {
  totalAmount?: unknown;
  retailPricingJson?: RetailPricingJson | null;
  invoiceGstEnabled?: boolean | null;
  computedProposalTotal?: unknown;
  lineItemTotalSum?: unknown;
  snapshotProposalTotal?: unknown;
}): ProposalApprovalAuditAmounts {
  const display = resolveProposalCustomerPricingDisplay(input);
  return {
    proposalTotal: display.proposalTotal,
    finalAgreedPrice: display.finalAgreedPrice,
    discountAmount: display.discountAmount,
    discountPercentage: display.discountPercentage,
    showDiscount: display.showDiscount,
    showDiscountPercentage: display.showDiscountPercentage,
    gstAmount: display.gstAmount,
    finalPayableAmount: display.finalAmountPayable,
    invoiceGstEnabled: display.invoiceGstEnabled,
  };
}

/** Scale proposal-level commercial pricing to a single option/system total (customer summary + approval modal). */
export function scaleProposalCustomerPricingForSystemTotal(
  pricing: ProposalCustomerPricingDisplay,
  systemTotal: number,
): ProposalCustomerPricingDisplay {
  const baseTotal = pricing.proposalTotal > 0 ? pricing.proposalTotal : systemTotal;
  const ratio = baseTotal > 0 ? systemTotal / baseTotal : 1;
  const proposalTotal = roundMoney(systemTotal);
  const finalAgreedPrice = pricing.showFinalAgreedPrice
    ? roundMoney(pricing.finalAgreedPrice * ratio)
    : roundMoney(systemTotal);
  const discountAmount = roundMoney(Math.max(0, proposalTotal - finalAgreedPrice));
  const showDiscount = discountAmount > 0.004;
  const discountPercentage =
    proposalTotal > 0.004 ? roundMoney((discountAmount / proposalTotal) * 100) : 0;
  return {
    ...pricing,
    proposalTotal,
    finalAgreedPrice,
    discountAmount,
    discountPercentage,
    showDiscount,
    showDiscountPercentage: showDiscount && pricing.showDiscountPercentage,
    showFinalAgreedPrice: showDiscount,
    gstAmount: pricing.showGst ? roundMoney(pricing.gstAmount * ratio) : pricing.gstAmount,
    finalAmountPayable: pricing.showFinalAmountPayable
      ? roundMoney(pricing.finalAmountPayable * ratio)
      : pricing.finalAmountPayable,
  };
}
